import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';

interface ContentRow {
  key: string;
  locale: string;
  value: string;
}

// Same fetch-once/in-memory-cache pattern as useSiteImage() — the whole
// (small) rxk_site_content table is fetched once and shared across every
// caller, so a missing row or failed fetch never breaks a page, it just
// falls back to whatever the caller already had.
let cache: Record<string, string> | null = null;
let inFlight: Promise<Record<string, string>> | null = null;

// The table has grown past 2500 rows (all page copy across 3 locales,
// plus tracking/SEO singletons) — well past PostgREST's default 1000-row
// response cap, so an unbounded select silently truncated to whatever the
// server happened to return first. Any key landing after that cutoff
// (this is exactly how the customHeadCode feature above initially shipped
// broken) would just never load, with no error — .range() pages through
// every row explicitly instead of trusting a single request to return
// everything.
async function fetchContentMap(): Promise<Record<string, string>> {
  try {
    const PAGE_SIZE = 1000;
    const map: Record<string, string> = {};
    for (let from = 0; ; from += PAGE_SIZE) {
      const { data, error } = await supabase
        .from('rxk_site_content')
        .select('key, locale, value')
        .range(from, from + PAGE_SIZE - 1);
      if (error) throw error;
      const rows = (data as ContentRow[]) ?? [];
      for (const row of rows) map[`${row.key}:${row.locale}`] = row.value;
      if (rows.length < PAGE_SIZE) break;
    }
    cache = map;
    return map;
  } catch {
    return {};
  }
}

function loadContentMap(): Promise<Record<string, string>> {
  if (cache) return Promise.resolve(cache);
  if (!inFlight) inFlight = fetchContentMap();
  return inFlight;
}

export function useSiteContentValue(key: string, locale: string, fallback = ''): string {
  const [value, setValue] = useState(() => cache?.[`${key}:${locale}`] ?? fallback);

  useEffect(() => {
    let mounted = true;
    loadContentMap().then((map) => {
      const v = map[`${key}:${locale}`];
      if (mounted && v) setValue(v);
    });
    return () => {
      mounted = false;
    };
  }, [key, locale]);

  return value;
}

// Tracking IDs are singletons, not per-language content, so they're always
// stored/read under locale 'en' regardless of the visitor's current UI
// language — there's only ever one GA4/GTM/Pixel ID for the whole site.
export function useTrackingSettings() {
  const ga4Id = useSiteContentValue('tracking.ga4Id', 'en');
  const gtmId = useSiteContentValue('tracking.gtmId', 'en');
  const metaPixelId = useSiteContentValue('tracking.metaPixelId', 'en');
  const gscVerification = useSiteContentValue('tracking.gscVerification', 'en');
  const customHeadCode = useSiteContentValue('tracking.customHeadCode', 'en');
  return { ga4Id, gtmId, metaPixelId, gscVerification, customHeadCode };
}
